import TYPES from '@/types';

import { currencyFormat } from '@/vue-app/utils/currency';

// Application
import GetCustomerPersonalityProfileQuery
  from '@/modules/flagship/customer-investor-profiling/application/query/get-customer-personality-profile-query';
import GetInvestorProfileQuery
  from '@/modules/flagship/investor-profile/investor-profile/application/queries/get-investor-profile-query';

// Domain
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import { DateFormatter } from '@/modules/shared/domain/date-formatters';

type Option = {
  label: string;
  value: string;
  key: string;
  show_edit_btn: boolean;
  component_to_edit: string;
}

type ProfileInfo = {
  label: string;
  description_1: string;
  description_2: string;
}

export default class UserProfilePersonalityProfileTabViewModel {
  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.GET_CUSTOMER_PERSONALITY_PROFILE_QUERY)
  private readonly get_customer_personality_profile!: GetCustomerPersonalityProfileQuery;

  @Inject(TYPES.GET_INVESTOR_PROFILE_QUERY)
  private readonly get_investor_profile_query!: GetInvestorProfileQuery;

  @Inject(TYPES.DATE_FORMATTER)
  readonly date_formatter!: DateFormatter;

  @Inject(TYPES.NOTIFIER)
  readonly message_notifier!: MessageNotifier;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.user-profile-personality-tab';

  is_loading = false;

  full_name = '';

  age = 0;

  profile_info: ProfileInfo = {
    label: '',
    description_1: '',
    description_2: '',
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  information_items: Array<Option> = [];

  last_updated_date = ''

  sections = this.translate('sections')

  is_main_dialog_edit_open = false;

  component_to_edit = '';

  pluralize = (count: number, noun: string, suffix = 's') => `${count} ${noun}${count !== 1 ? suffix : ''}`;

  getProfileInfo = (profile: string): ProfileInfo => {
    switch (profile) {
      case '1': return {
        label: `${this.translate('investor_profiles.1')}\n${this.translate('risk')}`,
        description_1: this.translate('description_investor_profiles.1.description_1'),
        description_2: this.translate('description_investor_profiles.1.description_2'),
      };
      case '2': return {
        label: `${this.translate('investor_profiles.2')}\n${this.translate('risk')}`,
        description_1: this.translate('description_investor_profiles.2.description_1'),
        description_2: this.translate('description_investor_profiles.2.description_2'),
      };
      case '3': return {
        label: `${this.translate('investor_profiles.3')}\n${this.translate('risk')}`,
        description_1: this.translate('description_investor_profiles.3.description_1'),
        description_2: this.translate('description_investor_profiles.3.description_2'),
      };
      default: return {
        label: '',
        description_1: '',
        description_2: '',
      };
    }
  }

  isPersonalityProfileSection = (key: string) => key === 'personality_profile'

  loadPersonalityProfile = async (load_state: boolean) => {
    try {
      const personality_profile = await this.get_customer_personality_profile.execute(load_state);
      this.full_name = personality_profile.full_name;
      this.age = personality_profile.age;
      if (personality_profile.investor_profile_completed) {
        this.last_updated_date = this.date_formatter.formatDate(personality_profile.updated_at, 'DD/MM/YYYY');
        let dependents = '';
        if (personality_profile.child_dependent_count) {
          dependents = `${dependents}${this.pluralize(personality_profile.child_dependent_count, this.translate('child'), 'es')}`;
        }
        if (personality_profile.elder_dependent_count) {
          if (personality_profile.child_dependent_count) {
            dependents = `${dependents}\n`;
          }
          dependents = `${dependents}${this.pluralize(personality_profile.elder_dependent_count, this.translate('elder'))}`;
        }
        if (personality_profile.pet_dependent_count) {
          // eslint-disable-next-line max-len
          if (personality_profile.elder_dependent_count || personality_profile.child_dependent_count) {
            dependents = `${dependents}\n`;
          }
          dependents = `${dependents}${this.pluralize(personality_profile.pet_dependent_count, this.translate('pet'))}`;
        }
        this.information_items.push(
          {
            label: this.translate('economic_dependents'), value: dependents, key: 'personal_context', show_edit_btn: true, component_to_edit: 'economic_dependents',
          },
        );
        this.information_items.push(
          {
            label: this.translate('employment_situation'),
            value: personality_profile.employment_situation,
            key: 'personal_context',
            show_edit_btn: true,
            component_to_edit: 'employment_situation',
          },
        );
        this.information_items.push(
          {
            label: this.translate('behavior_of_my_income'),
            value: personality_profile.income_behavior_in_following_year,
            key: 'financial_capacity',
            show_edit_btn: false,
            component_to_edit: '',
          },
        );
        this.information_items.push(
          {
            label: this.translate('monthly_savings_capacity'),
            value: `${currencyFormat(personality_profile.monthly_savings_capacity)} MXN`,
            key: 'financial_capacity',
            show_edit_btn: true,
            component_to_edit: 'monthly_savings_capacity',
          },
        );
      }
      this.information_items.push(
        {
          label: this.translate('investment_experience'),
          value: personality_profile.investment_experiences,
          key: 'investment_experience',
          show_edit_btn: true,
          component_to_edit: 'investment_experience',
        },
      );
      this.information_items.push(
        {
          label: this.translate('education_level'),
          value: personality_profile.education_level,
          key: 'personal_context',
          show_edit_btn: true,
          component_to_edit: 'education_level',
        },
      );
      if (personality_profile.personality_profile) {
        this.profile_info = this.getProfileInfo(personality_profile.personality_profile);
        this.information_items.push({
          label: this.translate('investor_profile'),
          value: this.profile_info.label,
          key: 'personality_profile',
          show_edit_btn: false,
          component_to_edit: '',
        });
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.load_personality_profile'));
    }
  }

  reloadInformation = async () => {
    this.information_items = [];
    await this.loadPersonalityProfile(false);
  }

  openEditMainDialog = (component_to_edit: string) => {
    this.component_to_edit = component_to_edit;
    this.is_main_dialog_edit_open = true;
  }

  initialize = async () => {
    this.is_loading = true;
    await this.loadPersonalityProfile(true);
    this.is_loading = false;
  }
}
